<div class="policy-container">

  <h1>Privacy Policy</h1>
  <p class="last-updated">Last Updated: May 17, 2026</p>

  <p>
    This Privacy Policy describes how xoomtag llc, doing business as Chareety
    ("<strong>Chareety</strong>," "<strong>we</strong>," "<strong>us</strong>," or "<strong>our</strong>"),
    collects, uses, stores, and shares your personal information when you use our services, including:
  </p>
  <ul>
    <li>Our websites at <strong>chareety.com</strong>, <strong>onboard.chareety.com</strong>, <strong>partners.chareety.com</strong>, and <strong>my.chareety.com</strong></li>
    <li>Organization admin consoles hosted on organization subdomains</li>
    <li>The DonationKiosk mobile application (Android and iOS)</li>
    <li>Any other Chareety-branded services (collectively, the "<strong>Services</strong>")</li>
  </ul>
  <p>
    By using our Services, you agree to the collection and use of information as described in this Privacy Policy.
    If you do not agree, please do not use our Services.
  </p>

  <h2>Table of Contents</h2>
  <ol class="toc">
    <li><a (click)="scrollTo('info-collect')">Information We Collect</a></li>
    <li><a (click)="scrollTo('how-use')">How We Use Your Information</a></li>
    <li><a (click)="scrollTo('how-share')">How We Share Your Information</a></li>
    <li><a (click)="scrollTo('sub-processors')">Sub-Processors and Third-Party Services</a></li>
    <li><a (click)="scrollTo('data-storage')">Data Storage and Security</a></li>
    <li><a (click)="scrollTo('data-retention')">Data Retention</a></li>
    <li><a (click)="scrollTo('cookies')">Cookies and Tracking</a></li>
    <li><a (click)="scrollTo('your-rights')">Your Privacy Rights</a></li>
    <li><a (click)="scrollTo('california')">California Privacy Rights (CCPA)</a></li>
    <li><a (click)="scrollTo('children')">Children's Privacy</a></li>
    <li><a (click)="scrollTo('dnt')">Do-Not-Track Signals</a></li>
    <li><a (click)="scrollTo('changes')">Changes to This Privacy Policy</a></li>
    <li><a (click)="scrollTo('contact')">How to Contact Us</a></li>
  </ol>

  <!-- Section 1 -->
  <h2 id="info-collect">1. Information We Collect</h2>

  <h3>1.1 Information You Provide Directly</h3>

  <h4>Donor Information</h4>
  <p>When you make a donation or create a donor account on MyChareety, we may collect:</p>
  <ul>
    <li>Name, email address, phone number, and mailing address</li>
    <li>Donation amounts, dates, and frequency (one-time or recurring)</li>
    <li>Subscription and recurring donation preferences</li>
    <li>Payment information (processed securely by Stripe &mdash; Chareety never receives or stores your full credit card number, debit card number, or bank account number)</li>
  </ul>

  <h4>Organization Information</h4>
  <p>When an organization signs up through our onboarding process, we collect:</p>
  <ul>
    <li>Organization name, EIN (Employer Identification Number), and address</li>
    <li>Authorized representative details (name, title, and the last four digits of SSN for Stripe identity verification)</li>
    <li>Banking and financial information (collected and stored by Stripe Connect &mdash; Chareety does not store bank account details directly)</li>
    <li>Branding assets such as logos and color preferences</li>
    <li>Email and SMS configuration preferences</li>
  </ul>

  <h4>Partner Information</h4>
  <p>When you register as a referral partner, we collect:</p>
  <ul>
    <li>Name, email address, and phone number</li>
    <li>Referral codes and referral history</li>
    <li>Commission and payment history</li>
    <li>Payment details for commission payouts</li>
  </ul>

  <h3>1.2 Information Collected Automatically</h3>
  <p>When you use our Services, we may automatically collect:</p>
  <ul>
    <li>IP address, browser type, operating system, and device information</li>
    <li>Pages visited, time spent, and actions taken within our Services</li>
    <li>Firebase Analytics data (basic app usage metrics)</li>
  </ul>

  <h3>1.3 Information from DonationKiosk Devices</h3>
  <p>The DonationKiosk app requests the following device permissions:</p>
  <ul>
    <li><strong>Bluetooth:</strong> To connect to Stripe card readers for payment processing</li>
    <li><strong>Location (when in use):</strong> Required by the Stripe SDK for payment compliance and fraud prevention</li>
    <li><strong>NFC (Android only):</strong> To support tap-to-pay card transactions</li>
    <li><strong>Device heartbeat data:</strong> The kiosk sends periodic status updates (every 15 minutes) to monitor device health</li>
  </ul>
  <p>
    The DonationKiosk does <strong>not</strong> access your camera, microphone, biometric sensors,
    or background location.
  </p>

  <!-- Section 2 -->
  <h2 id="how-use">2. How We Use Your Information</h2>
  <p>We use the information we collect to:</p>
  <ul>
    <li>Process donations and payment transactions on behalf of organizations</li>
    <li>Provide donors with donation history, tax receipts, and account management through MyChareety</li>
    <li>Create and manage donor, organization, and partner accounts</li>
    <li>Send transactional communications (receipts, account notifications, subscription updates)</li>
    <li>Monitor and maintain DonationKiosk device health and connectivity</li>
    <li>Calculate and process partner referral commissions</li>
    <li>Generate analytics and reporting for organizations about their donation activity</li>
    <li>Prevent fraud and ensure platform security</li>
    <li>Comply with legal obligations, including tax and financial reporting requirements</li>
    <li>Improve and maintain the performance of our Services</li>
  </ul>

  <!-- Section 3 -->
  <h2 id="how-share">3. How We Share Your Information</h2>

  <h3>3.1 With Organizations</h3>
  <p>
    When you make a donation to an organization through Chareety, your donor information
    (name, email, donation amount, and date) is shared with that organization. The organization
    is the <strong>data controller</strong> for donor data collected through its use of Chareety.
    Chareety acts as a <strong>data processor</strong> on behalf of the organization.
  </p>

  <h3>3.2 With Service Providers</h3>
  <p>
    We share information with third-party service providers who help us operate our Services.
    These providers are contractually obligated to use your information only to provide services
    to us and are listed in <a (click)="scrollTo('sub-processors')">Section 4</a>.
  </p>

  <h3>3.3 For Legal Reasons</h3>
  <p>We may disclose your information if required by law, regulation, legal process, or governmental request, or to protect our rights, safety, or property.</p>

  <h3>3.4 Business Transfers</h3>
  <p>If Chareety is involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you of any such change.</p>

  <h3>3.5 We Do Not Sell Your Data</h3>
  <p>
    Chareety does <strong>not</strong> sell, rent, or trade your personal information to third parties
    for marketing or advertising purposes. We have not sold personal information in the preceding
    twelve (12) months and will not do so in the future.
  </p>

  <!-- Section 4 -->
  <h2 id="sub-processors">4. Sub-Processors and Third-Party Services</h2>
  <p>We use the following third-party service providers to operate our platform:</p>

  <table>
    <thead>
      <tr>
        <th>Provider</th>
        <th>Purpose</th>
        <th>Data Processed</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>Google Cloud / Firebase</td>
        <td>Infrastructure, authentication, database, file storage, app analytics</td>
        <td>All platform data (encrypted at rest and in transit)</td>
      </tr>
      <tr>
        <td>Stripe</td>
        <td>Payment processing, organization Connect accounts</td>
        <td>Payment card data, bank details, identity verification for organizations</td>
      </tr>
      <tr>
        <td>Amazon Web Services (AWS SES)</td>
        <td>Transactional email delivery</td>
        <td>Email addresses, email content (receipts, notifications)</td>
      </tr>
      <tr>
        <td>Brevo</td>
        <td>Email and SMS communications (configured per organization)</td>
        <td>Email addresses, phone numbers, message content</td>
      </tr>
      <tr>
        <td>Hologram</td>
        <td>Cellular connectivity for DonationKiosk devices</td>
        <td>SIM identifiers, data usage metrics</td>
      </tr>
      <tr>
        <td>GitHub</td>
        <td>CI/CD infrastructure and code hosting</td>
        <td>No personal user data</td>
      </tr>
    </tbody>
  </table>

  <p>
    This site is protected by <strong>reCAPTCHA Enterprise</strong> and the Google
    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener">Privacy Policy</a> and
    <a href="https://policies.google.com/terms" target="_blank" rel="noopener">Terms of Service</a> apply.
  </p>

  <!-- Section 5 -->
  <h2 id="data-storage">5. Data Storage and Security</h2>

  <h3>5.1 Where We Store Data</h3>
  <p>All data is stored in the United States on Google Cloud infrastructure. Each organization's donor data is stored in an isolated Firebase project, ensuring data separation between organizations.</p>

  <h3>5.2 How We Protect Data</h3>
  <ul>
    <li><strong>Encryption:</strong> All data is encrypted in transit (TLS/SSL) and at rest (Google Cloud default encryption)</li>
    <li><strong>App Check:</strong> Firebase App Check with reCAPTCHA Enterprise validates that requests come from legitimate app instances</li>
    <li><strong>Firestore Security Rules:</strong> Three-tier access control (public, authenticated, backend-only) enforces per-document permissions</li>
    <li><strong>Per-Organization Isolation:</strong> Each organization receives its own Firebase project with separate authentication, database, and storage</li>
    <li><strong>No Card Data:</strong> Chareety never receives, processes, or stores full credit card numbers. All payment data is handled directly by Stripe, which is PCI DSS Level 1 certified</li>
  </ul>
  <p>
    While we implement commercially reasonable security measures, no method of electronic
    transmission or storage is 100% secure. We cannot guarantee absolute security of your data.
  </p>

  <!-- Section 6 -->
  <h2 id="data-retention">6. Data Retention</h2>
  <ul>
    <li>
      <strong>Donor account data</strong> is retained for as long as your donor account exists,
      even if an organization you donated to leaves the platform. This ensures you can continue
      to access your complete donation history and tax receipts through MyChareety.
    </li>
    <li>
      <strong>Organization data</strong> is deleted when an organization terminates its account
      and its dedicated Firebase project is torn down.
    </li>
    <li>
      <strong>Financial records</strong> may be retained for up to seven (7) years to comply
      with tax, accounting, and legal requirements.
    </li>
    <li>
      <strong>Account deletion:</strong> You may request deletion of your personal data at any time
      by contacting us at <a href="mailto:privacy@chareety.com">privacy&#64;chareety.com</a>.
      We will process your request within 45 days, subject to any legal retention obligations.
    </li>
  </ul>

  <!-- Section 7 -->
  <h2 id="cookies">7. Cookies and Tracking</h2>
  <p>We use cookies in a limited capacity:</p>
  <ul>
    <li><strong>Essential cookies:</strong> Session management and authentication</li>
    <li><strong>Consent cookie:</strong> Remembers your cookie consent preference (stored in localStorage)</li>
  </ul>
  <p>
    We do <strong>not</strong> use cookies for advertising, retargeting, or cross-site tracking.
    We do not use Google Analytics, Mixpanel, or any third-party behavioral tracking tools.
    Basic app usage metrics are collected through Firebase Analytics, which does not track
    individual users across sites.
  </p>

  <!-- Section 8 -->
  <h2 id="your-rights">8. Your Privacy Rights</h2>
  <p>Depending on your location, you may have the following rights regarding your personal information:</p>
  <ul>
    <li><strong>Right to Know:</strong> You can request what personal information we have collected about you.</li>
    <li><strong>Right to Delete:</strong> You can request that we delete your personal information, subject to certain legal exceptions.</li>
    <li><strong>Right to Correct:</strong> You can request correction of inaccurate personal information.</li>
    <li><strong>Right to Opt Out of Sale:</strong> We do not sell your personal information, so there is nothing to opt out of.</li>
    <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your privacy rights.</li>
  </ul>
  <p>
    To exercise any of these rights, contact us at
    <a href="mailto:privacy@chareety.com">privacy&#64;chareety.com</a>.
    We will verify your identity and respond within 45 days.
  </p>
  <p>
    If you have a donor account, you can also review and update your information by logging
    into MyChareety at <strong>my.chareety.com</strong>.
  </p>

  <!-- Section 9 -->
  <h2 id="california">9. California Privacy Rights (CCPA)</h2>
  <p>
    If you are a California resident, the California Consumer Privacy Act (CCPA) provides
    you with additional rights regarding your personal information.
  </p>

  <h3>Categories of Personal Information Collected</h3>
  <table>
    <thead>
      <tr>
        <th>Category</th>
        <th>Examples</th>
        <th>Collected</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>A. Identifiers</td>
        <td>Name, email, phone, mailing address, account name</td>
        <td>Yes</td>
      </tr>
      <tr>
        <td>B. Personal information (CA Customer Records)</td>
        <td>Name, contact info, financial information</td>
        <td>Yes</td>
      </tr>
      <tr>
        <td>C. Protected classifications</td>
        <td>Gender, date of birth</td>
        <td>No</td>
      </tr>
      <tr>
        <td>D. Commercial information</td>
        <td>Donation history, transaction records, subscription details</td>
        <td>Yes</td>
      </tr>
      <tr>
        <td>E. Biometric information</td>
        <td>Fingerprints, voiceprints</td>
        <td>No</td>
      </tr>
      <tr>
        <td>F. Internet or network activity</td>
        <td>Browsing history, interactions with our Services</td>
        <td>Limited (Firebase Analytics only)</td>
      </tr>
      <tr>
        <td>G. Geolocation data</td>
        <td>Device location</td>
        <td>Limited (DonationKiosk only, when in use, for Stripe compliance)</td>
      </tr>
      <tr>
        <td>H. Sensory data</td>
        <td>Audio, video, images</td>
        <td>No</td>
      </tr>
      <tr>
        <td>I. Professional/employment info</td>
        <td>Job title (organization representatives only)</td>
        <td>Limited</td>
      </tr>
      <tr>
        <td>J. Education information</td>
        <td>Student records</td>
        <td>No</td>
      </tr>
      <tr>
        <td>K. Inferences</td>
        <td>Profiles or predictions about preferences</td>
        <td>No</td>
      </tr>
    </tbody>
  </table>

  <p>
    <strong>We do not sell personal information.</strong> xoomtag llc has not sold any personal
    information in the preceding twelve (12) months and will not sell personal information
    in the future.
  </p>
  <p>
    To exercise your CCPA rights (right to know, right to delete, right to opt out),
    contact us at <a href="mailto:privacy@chareety.com">privacy&#64;chareety.com</a>.
    We will verify your identity before processing your request.
  </p>

  <!-- Section 10 -->
  <h2 id="children">10. Children's Privacy</h2>
  <p>
    Our Services are intended for users who are at least 18 years old. We do not knowingly
    collect personal information from anyone under 18. If we learn that we have collected
    personal information from a person under 18, we will delete that information promptly.
    If you believe we have collected information from a minor, please contact us at
    <a href="mailto:privacy@chareety.com">privacy&#64;chareety.com</a>.
  </p>

  <!-- Section 11 -->
  <h2 id="dnt">11. Do-Not-Track Signals</h2>
  <p>
    Some browsers include a Do-Not-Track (DNT) signal. Because there is no accepted standard
    for how to respond to DNT signals, we do not currently respond to them. If a uniform
    standard is adopted, we will update this policy accordingly.
  </p>

  <!-- Section 12 -->
  <h2 id="changes">12. Changes to This Privacy Policy</h2>
  <p>
    We may update this Privacy Policy from time to time. The updated version will be indicated
    by the "Last Updated" date at the top of this page. If we make material changes, we may
    notify you by posting a prominent notice on our Services. Your continued use of our
    Services after any changes constitutes your acceptance of the updated Privacy Policy.
  </p>

  <!-- Section 13 -->
  <h2 id="contact">13. How to Contact Us</h2>
  <p>If you have questions or concerns about this Privacy Policy or our data practices, contact us at:</p>
  <p>
    <strong>xoomtag llc, doing business as Chareety</strong><br>
    Bartlett, IL 60103<br>
    Bartlett, IL 60103<br>
    United States
  </p>
  <p>
    Email: <a href="mailto:privacy@chareety.com">privacy&#64;chareety.com</a>
  </p>

</div>
